import { Guid } from "@/Common/Models/Guid";
import { PharmacyOrderMedicationStatus } from "@/Common/Models/UI/Enums";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationDosageDisplayMode, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { IPharmacyOrderMedication } from "../Api/Dto/IPharmacyOrderMedication";
import { PharmacyOrderMedicationDosageModel } from "./PharmacyOrderMedicationDosageModel";

export class PharmacyOrderMedicationModel {
    public prescribedMedicationId: Guid;
    public pharmacyOrderId: Guid;
    public name: string;
    public form: string;
    public din: string;
    public strength: string;
    public strengthUnit: string;
    public duration: number;
    public route: string;
    public instruction: string;
    public numberOfRefill: number;
    public refillQuantity: string;
    public prescribeDate: Date;
    public vigilanceActiveIngredientCode: string;
    public dosage: string;
    public dosageUnit: number;
    public dosageTypeLookupId: Guid;
    public frequency: string;
    public status: PharmacyOrderMedicationStatus;
    public medicationStatus: number;
    public sourceMedicationId: Guid;
    public ceaseDate: Date | null;
    public refillStartDate: Date | null;
    public initialRefillQuantity: string | null;
    public totalDurationManual: number | null;
    public dosages: PharmacyOrderMedicationDosageModel[];
    public originalMedicationName: string | null;
    public ceaseReason: string | null;
    public originalMedicationCeaseDate: Date | null;
    public originalMedicationCeaseReason: string | null;

    public constructor(init?: Partial<PharmacyOrderMedicationModel>) {
        Object.assign(this, init);
    }

    public static fromResponseModel(model: IPharmacyOrderMedication): PharmacyOrderMedicationModel {
        return new PharmacyOrderMedicationModel({
            prescribedMedicationId: model.PrescribedMedicationId,
            pharmacyOrderId: model.PharmacyOrderId,
            name: model.Name,
            form: model.Form,
            din: model.DIN,
            strength: model.Strength,
            strengthUnit: model.StrengthUnit,
            route: model.Route,
            instruction: model.Instruction,
            numberOfRefill: model.NumberOfRefill,
            refillQuantity: model.RefillQuantity,
            prescribeDate: model.PrescribeDate,
            vigilanceActiveIngredientCode: model.VigilanceActiveIngredientCode,
            status: model.Status,
            medicationStatus: model.MedicationStatus,
            sourceMedicationId: model.SourceMedicationId,
            ceaseDate: model.CeaseDate,
            refillStartDate: model.RefillStartDate,
            initialRefillQuantity: model.InitialRefillQuantity,
            totalDurationManual: model.TotalDurationManual,
            dosages: model.Dosages?.map(PharmacyOrderMedicationDosageModel.fromResponseModel) ?? [],
            originalMedicationName: model.OriginalMedicationName,
            ceaseReason: model.CeaseReason,
            originalMedicationCeaseDate: model.OriginalMedicationCeaseDate,
            originalMedicationCeaseReason: model.OriginalMedicationCeaseReason
        });
    }

    public static toMedicationModel(model: PharmacyOrderMedicationModel): MedicationModel {
        const medicalItemMedicationAndPrescription = new MedicalItemMedicationAndPrescription();

        medicalItemMedicationAndPrescription.MedicalItemId = model.prescribedMedicationId;
        medicalItemMedicationAndPrescription.Name = model.name;
        medicalItemMedicationAndPrescription.Form = model.form ? model.form : null;
        medicalItemMedicationAndPrescription.DIN = model.din;
        medicalItemMedicationAndPrescription.DrugStrength = model.strength;
        medicalItemMedicationAndPrescription.StrengthUnit = model.strengthUnit;
        medicalItemMedicationAndPrescription.RefillDuration = model.duration;
        medicalItemMedicationAndPrescription.Route = model.route;
        medicalItemMedicationAndPrescription.Instruction = model.instruction;
        medicalItemMedicationAndPrescription.NumberOfRefill = model.numberOfRefill;
        medicalItemMedicationAndPrescription.RefillQuantity = model.refillQuantity;
        medicalItemMedicationAndPrescription.PrescribeDate = model.prescribeDate;
        medicalItemMedicationAndPrescription.RefillStartDate = model.refillStartDate;
        medicalItemMedicationAndPrescription.InitialRefillQuantity = model.initialRefillQuantity;
        medicalItemMedicationAndPrescription.TotalDurationManual = model.totalDurationManual;
        medicalItemMedicationAndPrescription.CodeGen = model.vigilanceActiveIngredientCode;
        medicalItemMedicationAndPrescription.pharmacyOrderMedicationStatus = model.status;
        medicalItemMedicationAndPrescription.Dosages = Immutable.List(
            model.dosages?.map(
                dosage =>
                    new MedicalItemMedicationAndPrescriptionDosage(
                        {
                            DosageFrequency: dosage.frequency,
                            DosageString: dosage.doseString,
                            DosageUnit: dosage.unit,
                            DosageTypeLookupId: dosage.typeLookupId,
                            Duration: dosage.duration,
                            DosageServedQuantity: dosage.servedQuantity,
                            DisplayMode: dosage.isQuantityMode ? MedicationDosageDisplayMode.Quantity : MedicationDosageDisplayMode.Duration,
                            RankPosition: dosage.rankPosition
                        }
                    )
            ) ?? []
        );
        if (model.ceaseDate) {
            medicalItemMedicationAndPrescription.CeaseDate = model.ceaseDate;
            medicalItemMedicationAndPrescription.CeaseReason = model.ceaseReason;
            medicalItemMedicationAndPrescription.PrescriptionStatusInt = model.medicationStatus;
            medicalItemMedicationAndPrescription.PrescriptionTab_PendingStatus = model.medicationStatus?.toString();
        }

        const medication = new MedicationModel(medicalItemMedicationAndPrescription);
        medication.RecalculateRefillEndDate();

        if (model.originalMedicationCeaseDate) {
            medication.PrescriptionTab_OriginalMedication = new MedicationModel(
                new MedicalItemMedicationAndPrescription({
                    Name: model.originalMedicationName,
                    CeaseDate: model.originalMedicationCeaseDate,
                    CeaseReason: model.originalMedicationCeaseReason,
                    PrescriptionStatusInt: model.medicationStatus,
                    PrescriptionTab_PendingStatus: model.medicationStatus?.toString()
                })
            );
        }

        return medication;
    }
}
